import { first } from 'lodash';

import { mapProvisionToRecipient } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { sortDispositiveProvisions } from '@/modules/dispositiveProvisions/DispositiveProvisionsListView/DispositiveProvisionsListView.utils';
import {
  DispositiveProvisionTemplateData_DispositiveProvisionTemplateFragment,
  DispositiveProvisionTemplateDataQueryHookResult,
  useDispositiveProvisionTemplateDataQuery,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsTemplateSplitScreenModal/graphql/DispositiveProvisionTemplateData.generated';
import { getNodes } from '@/utils/graphqlUtils';

import { TRANSFER_TAX_KIND_DISPLAY_NAME } from '../../DispositiveProvisionsForm.constants';

interface useDispositiveProvisionTemplateDisplayDataOutput
  extends DispositiveProvisionTemplateDataQueryHookResult {
  displayName: string;
  template:
    | DispositiveProvisionTemplateData_DispositiveProvisionTemplateFragment
    | undefined;
  /**
   * Recipients from the template, sorted and mapped to DispositiveProvisionTemplateRecipientDisplayData format.
   */
  recipientsAsDisplayData: DispositiveProvisionTemplateRecipientDisplayData[];
  /**
   * Recipients from the template, sorted and mapped to DispositiveProvisionsFormRecipient format.
   */
  recipientsAsFormData: DispositiveProvisionsFormRecipient[];
}

interface DispositiveProvisionTemplateRecipientDisplayData {
  id: string;
  displayName: string;
  kind: string;
  notes: string | null;
}

export function useDispositiveProvisionTemplateDisplayData(
  templateId: string | undefined | null
): useDispositiveProvisionTemplateDisplayDataOutput {
  const response = useDispositiveProvisionTemplateDataQuery({
    variables: {
      templateWhere: {
        id: templateId,
      },
    },
    skip: !templateId,
  });

  const template = first(
    getNodes(response.data?.dispositiveProvisionTemplates)
  );

  const provisions = getNodes(template?.dispositiveProvisions);

  const recipients = provisions
    .sort(sortDispositiveProvisions)
    .map<DispositiveProvisionTemplateRecipientDisplayData>((provision) => ({
      id: provision.id,
      displayName:
        provision.entity?.subtype?.displayName ||
        provision.testamentaryEntity?.displayName ||
        provision.individual?.displayName ||
        provision.organization?.name ||
        '',
      kind:
        (provision.transferTaxKind &&
          TRANSFER_TAX_KIND_DISPLAY_NAME[provision.transferTaxKind]) ??
        undefined,
      notes: provision.notes || null,
    }));

  return {
    ...response,
    displayName: template?.displayName || '',
    template,
    recipientsAsDisplayData: recipients,
    recipientsAsFormData: provisions
      .sort(sortDispositiveProvisions)
      .map(mapProvisionToRecipient),
  };
}
